import React, { useState, useContext } from 'react';
import axios from 'axios';
import { IoFingerPrintSharp, IoEye, IoEyeOff } from 'react-icons/io5';
import Toast from '../../components/Toast';
import { AuthContext } from '../../context/AuthContext';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent]         = useState(false);
  const [showNew, setShowNew]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [toast, setToast]                     = useState({ show: false, message: '', type: '' });
  const [loading, setLoading]                 = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const { token } = useContext(AuthContext); // assuming your AuthContext exposes token

  const validatePassword = (password) => {
    const hasUpperCase   = /[A-Z]/.test(password);
    const hasNumber      = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return { hasUpperCase, hasNumber, hasSpecialChar };
  };

  const passwordValidation = validatePassword(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setToast({ show: true, message: 'Passwords do not match', type: 'error' });
      return;
    }

    const { hasUpperCase, hasNumber, hasSpecialChar } = passwordValidation;
    if (!hasUpperCase || !hasNumber || !hasSpecialChar) {
      setToast({
        show: true,
        message: 'Password must contain at least one uppercase letter, one number, and one special character',
        type: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast({ show: true, message: 'Password changed successfully', type: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || 'Failed to change password',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-900 to-blue-950 px-4 sm:px-6 lg:px-8">
      <div className="text-center w-full max-w-md mx-auto">
        <div className="mb-6">
          <div className="w-12 h-12 mx-auto bg-gray-300 rounded-full flex items-center justify-center">
            <IoFingerPrintSharp className="h-10 w-10 font-teal-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mt-4">Change Password</h2>
          <p className="text-gray-300">Update your dashboard password</p>
        </div>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mb-4 relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current Password"
              className="w-64 p-3 bg-transparent border border-gray-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
            >
              {showCurrent ? <IoEyeOff className="w-5 h-5" /> : <IoEye className="w-5 h-5" />}
            </button>
          </div>

          <div className="mb-4 relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New Password"
              className="w-64 p-3 bg-transparent border border-gray-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
            >
              {showNew ? <IoEyeOff className="w-5 h-5" /> : <IoEye className="w-5 h-5" />}
            </button>
          </div>

          <div className="mb-2 text-left text-sm text-gray-300 w-64 mx-auto">
            <p className={passwordValidation.hasUpperCase ? 'text-green-400' : ''}>• At least one uppercase letter</p>
            <p className={passwordValidation.hasNumber ? 'text-green-400' : ''}>• At least one number</p>
            <p className={passwordValidation.hasSpecialChar ? 'text-green-400' : ''}>• At least one special character</p>
          </div>

          <div className="mb-4 relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm New Password"
              className="w-64 p-3 bg-transparent border border-gray-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-950 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
            >
              {showConfirm ? <IoEyeOff className="w-5 h-5" /> : <IoEye className="w-5 h-5" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-64 bg-white text-gray-800 font-semibold p-3 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 disabled:opacity-60"
          >
            {loading ? 'Updating…' : 'Change Password'}
          </button>
        </form>

        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ show: false, message: '', type: '' })}
          />
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
